import { PerformanceData } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { KeyShapeOptions } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { Channel4, GoalPacingColors, Success500 } from '@src/utils/colors';

export interface CrossChannelPacingTableData {
  data: PerformanceData[];
}

// Named value sets for the pacing view toggles.
export const Simplicity = { SIMPLE: 'Simple', ADJUSTED: 'Adjusted' } as const;
export type SimplicityType = (typeof Simplicity)[keyof typeof Simplicity];

export const TableView = { SUMMARY: 'Summary', OVER_TIME: 'Over Time' } as const;
export type TableViewType = (typeof TableView)[keyof typeof TableView];

export const DataMode = { RELATIVE: 'Relative', ABSOLUTE: 'Absolute' } as const;
export type DataModeType = (typeof DataMode)[keyof typeof DataMode];

// Details-modal chart series, keyed by the chart's dataKeys (plus the cumulative budget line). The
// chart draws each series with this colour, and its legend and tooltip key it with the same
// colour + ColorKey shape.
export const PacingDetailSeries = {
  // Cumulative (line) view
  cumulativeClearedSimpleSpend: { color: Channel4, shape: KeyShapeOptions.LINE },
  cumulativeProjectedSimpleSpend: { color: Channel4, shape: KeyShapeOptions.DASHED_LINE },
  cumulativeClearedSimpleIdealSpend: { color: Success500, shape: KeyShapeOptions.LINE },
  cumulativeProjectedAdjustedSpend: { color: Success500, shape: KeyShapeOptions.DASHED_LINE },
  budget: { color: GoalPacingColors.budget, shape: KeyShapeOptions.LINE },
  // Daily (bar) view
  clearedSimpleSpend: { color: Channel4, shape: KeyShapeOptions.SQUARE },
  clearedSimpleIdealSpend: { color: Success500, shape: KeyShapeOptions.SQUARE },
  projectedSimpleSpend: { color: GoalPacingColors.projected, shape: KeyShapeOptions.SQUARE },
  projectedAdjustedSpend: { color: GoalPacingColors.idealProjected, shape: KeyShapeOptions.SQUARE }
} as const;
export type PacingDetailSeriesKey = keyof typeof PacingDetailSeries;

// The Details chart's [actual, ideal] series per view and phase (cleared = before the data cutoff,
// projected = after it).
export const PacingDetailSeriesPairs = {
  cumulative: {
    Actual: ['cumulativeClearedSimpleSpend', 'cumulativeClearedSimpleIdealSpend'],
    Projected: ['cumulativeProjectedSimpleSpend', 'cumulativeProjectedAdjustedSpend']
  },
  daily: {
    Actual: ['clearedSimpleSpend', 'clearedSimpleIdealSpend'],
    Projected: ['projectedSimpleSpend', 'projectedAdjustedSpend']
  }
} as const satisfies Record<
  string,
  Record<string, readonly [PacingDetailSeriesKey, PacingDetailSeriesKey]>
>;

// Every [phase, [actualKey, idealKey]] pair across both views, to look up the hovered series.
export const SERIES_PAIRS = Object.values(PacingDetailSeriesPairs).flatMap(pairs =>
  Object.entries(pairs)
);

// One titled group of Details-legend items.
export interface LegendSection {
  title: string;
  items: { label: string; series: PacingDetailSeriesKey }[];
}

// Bar geometry (px, on the 4px grid): the chart container height and the bar thickness. Levels 3
// and 4 are the compact nested bars; the root/total bar is the tallest (outside the modal).
export interface BarSize {
  container: number;
  bar: number;
}
export const TOTAL_BAR_SIZE: BarSize = { container: 120, bar: 88 };
export const DEFAULT_BAR_SIZE: BarSize = { container: 92, bar: 60 };
export const BAR_SIZE_BY_LEVEL: Record<number, BarSize> = {
  3: { container: 72, bar: 36 },
  4: { container: 48, bar: 16 }
};

// Value labels: x inset from the bar start, and [cleared, projected] y offsets from the bar's top
// edge. Level 4's bar is too thin to hold text, so its labels sit above/below it instead.
export const LABEL_INSET = 8;
export const MODAL_LABEL_INSET = -4;
export const LABEL_Y_OFFSETS = {
  total: [4, 72],
  default: [4, 44],
  3: [4, 24],
  4: [-4, 28]
} as const;
export const COMPACT_LEVEL = 3;

// Room right of the bar for its end label: the modal's fixed gutter, or the label width + 56.
export const MODAL_RIGHT_GUTTER = 196;
export const END_LABEL_ALLOWANCE = 56;
// The goal label's gap from the budget line, and its font (.pacing-bar-goal-label) for measuring.
export const GOAL_LABEL_GAP = 4;
export const GOAL_LABEL_FONT = "600 14px 'Geist'";
// Width added to the measured label text for the logo and gap. The column ends at recharts' tick x,
// 8px (tickSize + tickMargin) left of the bar.
export const Y_LABEL_EXTRA_WIDTH = 40;
export const MODAL_BAR_NUDGE = 16;
